import React, { useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import AnnotationBody, { AnnotationAvatar } from './AnnotationBody';
import AnnotationComposer from './AnnotationComposer';
import { useAppDialog } from 'shared/context/AppDialogContext';
import { formatRelativeTime, formatDate } from 'features/agent-analytics/components/runCells';

/**
 * Replies — indented under the comment they answer, folded until asked for.
 *
 * A reply used to render at the same indent as the comment, in the same
 * column, so a thread read as a flat run of messages with no way to see what
 * answered what. It is now offset under its parent behind a hairline, which
 * is what makes a thread legible at a glance.
 *
 * And it is folded. Comments accumulate on the runs people argue about, and
 * a span's Activity should not open with forty of them; "3 replies" opens
 * them, and opens the box to add one. A comment with no replies shows only
 * "Reply", which is the whole affordance until there is a thread to see.
 */
function Reply({ reply, currentUser, onEdit, onDelete }) {
  const { confirm: confirmDialog } = useAppDialog();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const isOwn = currentUser?.id != null && Number(reply.author?.id) === Number(currentUser.id);

  return (
    <div className={`flex items-start gap-2 ${reply.pending ? 'opacity-60' : ''}`}>
      <AnnotationAvatar name={reply.author?.name} src={reply.author?.avatarUrl} size={20} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[12px] font-medium text-[color:var(--text-primary)]">
            {reply.author?.name}
          </span>
          <span
            className="flex-none text-[12px] text-[color:var(--text-muted)]"
            title={formatDate(reply.createdAt)}
          >
            {reply.pending ? 'Sending…' : formatRelativeTime(reply.createdAt)}
          </span>
          {reply.edited ? (
            <span className="flex-none text-[12px] text-[color:var(--text-muted)]">Edited</span>
          ) : null}
          {isOwn && !reply.pending ? (
            <div className="relative ml-auto flex-none">
              <button
                type="button"
                aria-label="Reply actions"
                onClick={() => setMenuOpen((v) => !v)}
                className="flex h-5 w-5 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
              >
                <MoreHorizontal size={14} />
              </button>
              {menuOpen ? (
                <>
                  <button
                    type="button"
                    className="fixed inset-0 z-10 cursor-default"
                    aria-label="Close menu"
                    onClick={() => setMenuOpen(false)}
                  />
                  <div className="absolute right-0 z-20 mt-1 w-32 overflow-hidden rounded-lg border border-[color:var(--border-default)] bg-surface py-1 shadow-token-lg">
                    <button
                      type="button"
                      className="block w-full px-3 py-1.5 text-left text-[12px] text-[color:var(--text-primary)] hover:bg-surface-hover"
                      onClick={() => { setMenuOpen(false); setEditing(true); }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="block w-full px-3 py-1.5 text-left text-[12px] text-[color:var(--feedback-error)] hover:bg-surface-hover"
                      onClick={async () => {
                        setMenuOpen(false);
                        const ok = await confirmDialog('This reply will be removed.', {
                          title: 'Delete reply',
                          confirmLabel: 'Delete',
                          danger: true,
                        });
                        if (ok) onDelete?.(reply.id);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
        {editing ? (
          <div className="mt-1.5">
            <AnnotationComposer
              placeholder="Edit your reply..."
              submitLabel="Save"
              initialBody={reply.body}
              initialMentions={reply.mentions || []}
              onCancel={() => setEditing(false)}
              onSubmit={(payload) => {
                setEditing(false);
                onEdit?.(reply.id, payload);
              }}
            />
          </div>
        ) : (
          <AnnotationBody body={reply.body} mentions={reply.mentions} className="mt-0.5" />
        )}
      </div>
    </div>
  );
}

export default function AnnotationThread({
  annotation,
  currentUser,
  onReply,
  onEdit,
  onDelete,
}) {
  const replies = annotation.replies || [];
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[12px] font-medium text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
      >
        {replies.length === 0
          ? 'Reply'
          : `${replies.length} ${replies.length === 1 ? 'reply' : 'replies'}`}
      </button>

      {open ? (
        <div className="mt-2 flex flex-col gap-3 border-l border-[color:var(--border-subtle)] pl-3">
          {replies.map((reply) => (
            <Reply
              key={reply.id}
              reply={reply}
              currentUser={currentUser}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
          <AnnotationComposer
            variant="reply"
            autoFocus={replies.length === 0}
            context={annotation.context}
            conversationId={annotation.conversationId}
            placeholder="Leave a reply..."
            submitLabel="Reply"
            onSubmit={(payload) => onReply?.(annotation.id, payload)}
          />
        </div>
      ) : null}
    </div>
  );
}
